import { Plus } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox/Checkbox';
import { Input } from '@/shared/components/ui/Input/Input';
import { Label } from '@/shared/components/ui/Label/Label';
import { RadioGroup } from '@/shared/components/ui/RadioGroup/RadioGroup';
import { POPPER_SCROLLER_CLASSES } from '@/shared/components/ui/scroll/scroll.tokens';
import { cn } from '@/shared/utils/cn/cn.util';

import { SelectionOptionRow } from '../SelectionOptionRow/SelectionOptionRow';
import {
  SELECTION_FILTER_ROW_CLASSES,
  SELECTION_FOOTER_CLASSES,
  SELECTION_GROUP_HEADING_CLASSES,
  SELECTION_HEADER_ROW_CLASSES,
  SELECTION_LIST_CLASSES,
  SELECTION_NOTICE_CLASSES,
  SELECTION_PANEL_CLASSES,
  SELECTION_ROW_CLASSES,
} from '../selection.tokens';

import { type SelectionPanelProps } from './SelectionPanel.types';

/**
 * The one panel behind `MultiSelect`, `ServerSelect` and `ServerMultiSelect`.
 *
 * Presentation only (U5): everything it draws is decided by the caller's logic
 * hook. It exists so the three controls cannot drift — a fix to the empty state,
 * the cap notice or the keyboard story lands in all three at once, which is
 * exactly what did NOT happen while a coded-enum picker and a server-backed one
 * were two unrelated files.
 *
 * Every state it can be in is here and none of them is a blank box: loading,
 * error with a retry, empty, capped, at-the-limit, and a live region that
 * announces what a list mutation cannot (screen readers do not announce rows
 * appearing).
 */
export function SelectionPanel({
  mode,
  filter,
  list,
  selectAll,
  create,
  status,
  footer,
  loadMore,
  announcement,
  singleValue,
  listRef,
  sentinelRef,
  onKeyDown,
}: SelectionPanelProps) {
  const { t } = useTranslation('uiKit');
  const filterId = useId();
  const selectAllId = useId();
  const limitHintId = useId();

  const hasLimit = footer?.maxSelected !== undefined;
  const limitHint = hasLimit ? t('multiSelect.limit', { max: footer.maxSelected }) : undefined;

  // The caller's own announcement wins; otherwise the panel narrates whichever
  // state it is showing, so a blank list is never a silent one.
  const liveMessage =
    announcement !== ''
      ? announcement
      : status.isLoading
        ? t('multiSelect.loading')
        : status.isError
          ? t('multiSelect.error')
          : '';

  const rows = list.sections.map((section) => (
    <div key={section.group?.id ?? '__ungrouped'} className="flex flex-col">
      {section.group === null ? null : (
        <p className={SELECTION_GROUP_HEADING_CLASSES}>{section.group.label}</p>
      )}
      {section.options.map((option) => (
        <SelectionOptionRow
          key={option.value}
          option={option}
          mode={mode}
          isSelected={list.selected.has(option.value)}
          isActive={list.activeValue === option.value}
          isCreated={list.createdValues.includes(option.value)}
          isDisabled={list.isOptionDisabled(option)}
          isReadOnly={list.isReadOnly}
          describedById={hasLimit ? limitHintId : undefined}
          onSelect={list.onSelect}
          onKeyDown={onKeyDown}
          renderOption={list.renderOption}
        />
      ))}
    </div>
  ));

  return (
    <div className={SELECTION_PANEL_CLASSES}>
      <div className={SELECTION_FILTER_ROW_CLASSES}>
        <Label htmlFor={filterId} className="sr-only">
          {filter.placeholder ?? t('multiSelect.searchPlaceholder')}
        </Label>
        <Input
          id={filterId}
          value={filter.term}
          placeholder={filter.placeholder ?? t('multiSelect.searchPlaceholder')}
          autoComplete="off"
          // The roving handler is bound to the filter and to each ROW, never to
          // a container: an arrow key pressed in the filter must walk into the
          // options (the combobox gesture) and a keydown does not travel DOWN to
          // a sibling, while a handler on the static panel root or the
          // `role="group"` list is a jsx-a11y violation in both directions.
          // Both elements it lands on are natively interactive.
          onKeyDown={onKeyDown}
          onChange={(event) => {
            filter.onChange(event.target.value);
          }}
        />
      </div>

      {selectAll === undefined ? null : (
        <div className={SELECTION_HEADER_ROW_CLASSES}>
          <Checkbox
            id={selectAllId}
            checked={
              selectAll.state === 'indeterminate' ? 'indeterminate' : selectAll.state === 'checked'
            }
            disabled={selectAll.isDisabled}
            onCheckedChange={selectAll.onToggle}
          />
          {/* The label changes with the scope it acts on. Acting on the whole
              list while showing a subset is the single most-reported "it
              selected things I could not see" defect. */}
          <Label htmlFor={selectAllId} className="cursor-pointer font-normal">
            {selectAll.isNarrowed ? t('multiSelect.selectAllVisible') : t('multiSelect.selectAll')}
          </Label>
        </div>
      )}

      {list.isReadOnly ? (
        <p className={cn(SELECTION_NOTICE_CLASSES, 'shrink-0 border-b border-border py-sm')}>
          {t('multiSelect.readOnly')}
        </p>
      ) : null}

      <div
        ref={listRef}
        // No `aria-readonly` here: `group` does not support it (only
        // `radiogroup`, `checkbox`, `listbox`, `textbox` and friends do), so it
        // would be an inert attribute pretending to be half a fix. Each checkbox
        // ROW carries its own, and `checkbox` does support it.
        role={mode === 'multi' ? 'group' : undefined}
        className={cn(SELECTION_LIST_CLASSES, POPPER_SCROLLER_CLASSES)}
      >
        {status.isLoading ? (
          (status.loadingContent ?? (
            <p className={SELECTION_NOTICE_CLASSES}>{t('multiSelect.loading')}</p>
          ))
        ) : status.isError ? (
          (status.errorContent ?? (
            <div className={SELECTION_NOTICE_CLASSES}>
              <p>{t('multiSelect.error')}</p>
              {status.onRetry === undefined ? null : (
                <Button type="button" variant="ghost" size="sm" onClick={status.onRetry}>
                  {t('multiSelect.retry')}
                </Button>
              )}
            </div>
          ))
        ) : status.isEmpty && create?.candidate?.status !== 'ok' ? (
          (status.emptyContent ?? (
            <p className={SELECTION_NOTICE_CLASSES}>
              {status.emptyLabel ?? t('multiSelect.empty')}
            </p>
          ))
        ) : mode === 'single' ? (
          <RadioGroup
            // `''`, never `undefined`: Radix's `useControllableState` reads an
            // undefined `value` as UNCONTROLLED and starts tracking its own, so
            // an empty read-only picker would announce "nothing was changed"
            // and check the refused row in the same click. An empty string is
            // defined and matches no option.
            value={singleValue ?? ''}
            // ARIA supports `aria-readonly` on `radiogroup` and on `checkbox` —
            // so it goes on the GROUP here and on each ROW in the multi path.
            // It is not supported on a `radio`, where it would announce nothing.
            aria-readonly={list.isReadOnly ? true : undefined}
            onValueChange={list.onSelect}
            className="gap-0"
          >
            {rows}
          </RadioGroup>
        ) : (
          rows
        )}

        {create?.candidate == null ? null : (
          <button
            type="button"
            disabled={create.isDisabled || create.candidate.status === 'rejected'}
            onClick={create.onCreate}
            className={cn(SELECTION_ROW_CLASSES, 'w-full text-start disabled:opacity-50')}
          >
            <Plus aria-hidden className="mt-2xs h-4 w-4 shrink-0" />
            <span className="flex min-w-0 flex-1 flex-col gap-xs">
              {/* The action and the VALUE are separate elements, not one
                  interpolated sentence, and the value never breaks. An operator
                  adding `claude-opus-6` has to be able to read which model they
                  are committing to, and both single-line spellings failed at
                  320: `truncate` clipped it mid-identifier, and wrapping broke
                  it at its own hyphen into `claude-opus-` / `6` — the trap P10.4
                  already recorded as "an identifier is not prose". It wraps to
                  its own line instead, whole. */}
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-xs">
                <span>{t('multiSelect.create')}</span>
                <span dir="ltr" className="truncate whitespace-nowrap">
                  {create.candidate.status === 'ok' ? create.candidate.value : filter.term.trim()}
                </span>
              </span>
              {create.candidate.status === 'rejected' ? (
                <span className="text-caption text-muted-foreground">
                  {create.candidate.reason}
                </span>
              ) : null}
            </span>
          </button>
        )}

        {status.hiddenCount > 0 ? (
          <p className={SELECTION_NOTICE_CLASSES}>
            {t('multiSelect.capped', { count: status.hiddenCount })}
          </p>
        ) : null}

        {loadMore?.canLoadMore === true ? (
          <>
            {/* Two triggers for one clamped state change. The sentinel prefetches
                for a pointer; the button is what makes the rest of the list
                reachable for a keyboard, where arrow-down simply stops at the
                last rendered row. */}
            <div ref={sentinelRef} aria-hidden className="h-xs w-full shrink-0" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={loadMore.onLoadMore}
            >
              {t('serverSelect.loadMore')}
            </Button>
          </>
        ) : null}

        {loadMore?.isFetching === true ? (
          <p className={SELECTION_NOTICE_CLASSES}>{t('serverSelect.loadingMore')}</p>
        ) : null}
      </div>

      {footer === undefined ? null : (
        <div className={SELECTION_FOOTER_CLASSES}>
          {/* `aria-hidden`: the live region below carries every announcement, and
              a summary that re-reads itself on each toggle is noise. */}
          <span aria-hidden>
            {t(
              footer.isTotalPartial === true
                ? 'multiSelect.summaryLineLoaded'
                : 'multiSelect.summaryLine',
              { selected: footer.selectedCount, total: footer.totalCount },
            )}
          </span>
          {limitHint === undefined ? null : <span id={limitHintId}>{limitHint}</span>}
        </div>
      )}

      {/* The panel's ONE live region. Every visible notice above is a plain
          paragraph: two `role="status"` elements in one panel means a screen
          reader is handed two competing announcements for the same event, and
          the operator hears whichever the browser felt like reading. */}
      <p role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </p>
    </div>
  );
}
